import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  getApplicationIssuedDocumentDefaults,
  getApplicationIssuedDocuments,
  getApplicationDocumentRenderHtml,
  getIssuedDocumentDownload,
  getStudentDocumentDefaults,
  getStudentDocuments,
  issueApplicationDocument,
  issueStudentDocument,
  revokeIssuedDocument,
  saveApplicationDocument,
  confirmIssuedDocument,
  reopenIssuedDocument,
  restoreIssuedDocument,
} from '@/lib/api';
import {
  formatDateTimeValue,
  formatDateValue,
  toDateInputValue,
} from '@/lib/dates';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import StudentDocumentInvoices from '@/components/admin/StudentDocumentInvoices';
import { Ban, FileCheck2, FileDown, Highlighter, History, Loader2, Minus, Pencil, Plus, RefreshCw, Save } from 'lucide-react';

const inputClass =
  'border-[rgba(6, 43, 26,0.16)] focus:border-[#D4AF37] focus:ring-[rgba(212, 175, 55,0.25)]';

const defaultDocTypes = [
  { value: 'offer_letter', label: 'Offer Letter' },
  { value: 'enrolment_contract', label: 'Student Enrolment Contract' },
  { value: 'loa', label: 'Official Letter of Acceptance' },
  { value: 'transcript', label: 'Transcript' },
  { value: 'invoice', label: 'Invoice' },
  { value: 'receipt', label: 'Receipt' },
  { value: 'fee_payment_instructions', label: 'Fee Payment Instructions' },
];

const checklistStatusOptions = ['Received', 'Exempted', 'Pending'];
const yesNoOptions = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];
const inlineApplicationDocTypes = new Set(['offer_letter', 'enrolment_contract', 'fee_payment_instructions', 'invoice', 'receipt']);
const inlineStudentDocTypes = new Set(['offer_letter', 'enrolment_contract', 'fee_payment_instructions', 'invoice', 'receipt', 'loa', 'transcript']);
const financialInlineDocTypes = new Set(['invoice', 'receipt']);

function safeList(value) {
  return Array.isArray(value) ? value : [];
}

function formText(value) {
  return value === null || value === undefined ? '' : String(value);
}

function formDate(value) {
  return toDateInputValue(value);
}

function formNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? String(number) : '';
}

function formBool(value) {
  return value === true || value === 'true' ? 'true' : 'false';
}

function cleanError(err, fallback = 'Request failed') {
  const detail = err?.response?.data?.detail;
  const error = err?.response?.data?.error;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object') return detail.message || fallback;
  if (typeof error === 'string') return error;
  return fallback;
}

function displayText(value, fallback = 'Not set') {
  const text = formText(value).trim();
  return text || fallback;
}

function formatDate(value) {
  return formatDateValue(value, { fallback: 'Not set' });
}

function formatDateTime(value) {
  return formatDateTimeValue(value, { fallback: '' });
}

function newestDocument(history) {
  return safeList(history)[0] || null;
}

function titleCaseStatus(status) {
  if (status === 'draft') return 'Draft';
  if (status === 'issued') return 'Confirmed (Issued)';
  if (status === 'revoked') return 'Revoked';
  return displayText(status, 'Unknown');
}

function invoiceAmount(value) {
  const parsed = Number(String(value ?? '').replace(/[^0-9.-]/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatInvoiceAmount(value) {
  return `$${invoiceAmount(value).toLocaleString('en-CA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function invoiceLineItems(root) {
  return [...root.querySelectorAll('[data-line-item]')].map((line) => ({
    subject: line.querySelector('[data-line-field="subject"]')?.innerText.trim() || '',
    description: line.querySelector('[data-line-field="description"]')?.innerText.trim() || '',
    quantity: invoiceAmount(line.querySelector('[data-line-field="quantity"]')?.innerText),
    rate: invoiceAmount(line.querySelector('[data-line-field="rate"]')?.innerText),
  }));
}

function recalculateInvoice(root) {
  const lineItems = invoiceLineItems(root);
  const total = lineItems.reduce((sum, item) => sum + item.quantity * item.rate, 0);
  root.querySelectorAll('[data-line-item]').forEach((line, index) => {
    const item = lineItems[index];
    const gross = line.querySelector('[data-line-gross]');
    if (gross) gross.innerText = formatInvoiceAmount(item.quantity * item.rate);
  });
  root.querySelectorAll('[data-computed-total]').forEach((element) => {
    element.innerText = formatInvoiceAmount(total);
  });
}

export function issuedDocumentLabel(docTypes, docType) {
  return docTypes.find((doc) => doc.value === docType)?.label || 'Document';
}

export default function OfficialDocumentsCard({
  ownerType = 'student',
  ownerId,
  docTypes = defaultDocTypes,
  title = 'Documents',
  description = '',
}) {
  const isApplication = ownerType === 'application';
  const [documents, setDocuments] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [issueType, setIssueType] = useState('');
  const [issueInputs, setIssueInputs] = useState({});
  const [issueLoading, setIssueLoading] = useState(false);
  const [issueSubmitting, setIssueSubmitting] = useState(false);
  const [downloadingId, setDownloadingId] = useState('');
  const [revokingId, setRevokingId] = useState('');
  const [changingId, setChangingId] = useState('');
  const [historyOpen, setHistoryOpen] = useState({});
  const [offerEditor, setOfferEditor] = useState(null);
  const [offerEditorLoading, setOfferEditorLoading] = useState(false);
  const [offerEditorSaving, setOfferEditorSaving] = useState(false);
  const [showOfferHighlights, setShowOfferHighlights] = useState(true);
  const offerFrameRef = useRef(null);

  const loadDocuments = useCallback(async () => {
    if (!ownerId) return;
    setLoading(true);
    setError('');
    try {
      const res = isApplication
        ? await getApplicationIssuedDocuments(ownerId)
        : await getStudentDocuments(ownerId);
      setDocuments(res.data || {});
    } catch (err) {
      console.error(err);
      setError(cleanError(err, 'Documents could not be loaded.'));
    } finally {
      setLoading(false);
    }
  }, [isApplication, ownerId]);

  useEffect(() => {
    loadDocuments();
    window.addEventListener('portal-documents-changed', loadDocuments);
    return () => window.removeEventListener('portal-documents-changed', loadDocuments);
  }, [loadDocuments]);

  const updateIssueInput = (key, value) => {
    setIssueInputs((current) => ({ ...current, [key]: value }));
  };

  const updateListRow = (key, index, field, value) => {
    setIssueInputs((current) => {
      const rows = safeList(current[key]).map((row) => ({ ...row }));
      rows[index] = { ...(rows[index] || {}), [field]: value };
      return { ...current, [key]: rows };
    });
  };

  const addListRow = (key, row) => {
    setIssueInputs((current) => ({ ...current, [key]: [...safeList(current[key]), row] }));
  };

  const removeListRow = (key, index) => {
    setIssueInputs((current) => ({
      ...current,
      [key]: safeList(current[key]).filter((_, rowIndex) => rowIndex !== index),
    }));
  };

  const openIssueDialog = async (docType) => {
    setIssueType(docType);
    setIssueInputs({});
    setIssueLoading(true);
    try {
      const res = isApplication
        ? await getApplicationIssuedDocumentDefaults(ownerId, docType)
        : await getStudentDocumentDefaults(ownerId, docType);
      setIssueInputs(res.data?.inputs || {});
    } catch (err) {
      console.error(err);
      toast.error(cleanError(err, 'Document defaults could not be loaded.'));
      setIssueType('');
    } finally {
      setIssueLoading(false);
    }
  };

  const closeIssueDialog = () => {
    if (issueSubmitting) return;
    setIssueType('');
    setIssueInputs({});
  };

  const handleIssue = async () => {
    if (!issueType) return;
    const active = documents?.[issueType]?.active;
    const label = issuedDocumentLabel(docTypes, issueType);
    if (active) {
      const scope = isApplication ? 'application' : 'student';
      if (!window.confirm(`Generate a new ${label}? The previous active version will be archived for this ${scope}.`)) {
        return;
      }
    }

    try {
      setIssueSubmitting(true);
      if (isApplication) {
        await issueApplicationDocument(ownerId, { doc_type: issueType, inputs: issueInputs });
        toast.success(`${label} draft saved.`);
      } else {
        await issueStudentDocument(ownerId, { doc_type: issueType, inputs: issueInputs, as_draft: true });
        toast.success(`${label} draft saved.`);
      }
      setIssueType('');
      setIssueInputs({});
      await loadDocuments();
    } catch (err) {
      console.error(err);
      toast.error(cleanError(err, 'Document could not be generated.'));
    } finally {
      setIssueSubmitting(false);
    }
  };

  const handleDownload = async (documentId) => {
    try {
      setDownloadingId(documentId);
      const res = await getIssuedDocumentDownload(documentId);
      if (res.data?.url) {
        window.open(res.data.url, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      console.error(err);
      toast.error(cleanError(err, 'Download link could not be created.'));
    } finally {
      setDownloadingId('');
    }
  };

  const handleDocumentState = async (doc, action, active) => {
    const message = action === 'confirm'
      ? 'Confirm this version as issued? It stays editable and no email is sent.'
      : action === 'reopen'
        ? 'Return this document to draft? Sending history and saved payments remain. An invoice will stop counting as receivable until confirmed again.'
        : `Restore version ${doc.version} as a new ${active?.status === 'issued' ? 'confirmed' : 'draft'} version? Previous versions and sending history remain.`;
    if (!window.confirm(message)) return;
    setChangingId(doc.id);
    try {
      const work = action === 'confirm' ? confirmIssuedDocument : action === 'reopen' ? reopenIssuedDocument : restoreIssuedDocument;
      await work(doc.id);
      toast.success(action === 'confirm' ? 'Document confirmed.' : action === 'reopen' ? 'Document returned to draft.' : 'Earlier version restored as a new version.');
      await loadDocuments();
      window.dispatchEvent(new Event('portal-documents-changed'));
    } catch (err) {
      toast.error(cleanError(err, 'Document could not be updated.'));
    } finally { setChangingId(''); }
  };

  const handleRevoke = async (doc) => {
    if (!doc?.id) return;
    const message = isApplication
      ? 'Revoke this draft? It will remain in history, but it will not transfer to the student.'
      : 'Revoke this document? The student will lose access immediately, but admins can still download the archived version.';
    if (!window.confirm(message)) return;
    try {
      setRevokingId(doc.id);
      await revokeIssuedDocument(doc.id);
      toast.success(`${issuedDocumentLabel(docTypes, doc.doc_type)} revoked.`);
      await loadDocuments();
    } catch (err) {
      console.error(err);
      toast.error(cleanError(err, 'Document could not be revoked.'));
    } finally {
      setRevokingId('');
    }
  };

  const handleGenerateInlineDocument = async (docType) => {
    const label = issuedDocumentLabel(docTypes, docType);
    try {
      setIssueSubmitting(true);
      if (isApplication) {
        await issueApplicationDocument(ownerId, { doc_type: docType, inputs: {} });
        toast.success(`${label} draft generated.`);
      } else {
        await issueStudentDocument(ownerId, { doc_type: docType, inputs: {}, as_draft: true });
        toast.success(`${label} draft generated.`);
      }
      await loadDocuments();
    } catch (err) {
      console.error(err);
      toast.error(cleanError(err, `${label} could not be generated.`));
    } finally {
      setIssueSubmitting(false);
    }
  };

  const openOfferEditor = async (documentId) => {
    setOfferEditorLoading(true);
    setShowOfferHighlights(true);
    setOfferEditor({ documentId, html: '', label: 'Document', docType: '' });
    try {
      const res = await getApplicationDocumentRenderHtml(documentId);
      setOfferEditor({
        documentId,
        html: res.data?.html || '',
        label: res.data?.document?.file_name || 'Offer Letter',
        docType: res.data?.document?.doc_type || '',
        status: res.data?.document?.status || 'draft',
      });
    } catch (err) {
      console.error(err);
      toast.error(cleanError(err, 'Document editor could not be loaded.'));
      setOfferEditor(null);
    } finally {
      setOfferEditorLoading(false);
    }
  };

  const closeOfferEditor = () => {
    if (!offerEditorSaving) setOfferEditor(null);
  };

  const handleOfferFrameLoad = () => {
    const frameDocument = offerFrameRef.current?.contentDocument;
    const root = frameDocument?.querySelector('[data-document-editor]');
    if (!frameDocument || !root) return;
    const isFinancialDocument = financialInlineDocTypes.has(root.dataset.documentType);
    root.classList.toggle('show-edit', showOfferHighlights);
    if (isFinancialDocument) recalculateInvoice(root);
    root.addEventListener('input', (event) => {
      const source = event.target;
      const field = source?.getAttribute?.('data-field');
      if (field) {
        const value = source.innerText;
        frameDocument.querySelectorAll(`[data-field="${field}"]`).forEach((target) => {
          if (target !== source) target.innerText = value;
        });
      }
      if (isFinancialDocument && source?.hasAttribute?.('data-line-field')) {
        recalculateInvoice(root);
      }
    });
    root.addEventListener('click', (event) => {
      if (!isFinancialDocument) return;
      const target = event.target;
      const addLine = target?.closest?.('[data-add-line]');
      if (addLine) {
        const template = frameDocument.getElementById('invoice-line-item-template');
        const lineItems = frameDocument.getElementById('invoice-line-items');
        if (template?.content && lineItems) {
          lineItems.append(template.content.cloneNode(true));
          recalculateInvoice(root);
        }
        return;
      }
      const removeLine = target?.closest?.('[data-remove-line]');
      if (removeLine) {
        removeLine.closest('[data-line-item]')?.remove();
        recalculateInvoice(root);
      }
    });
  };

  const toggleOfferHighlights = () => {
    setShowOfferHighlights((current) => {
      const next = !current;
      offerFrameRef.current?.contentDocument?.querySelector('[data-document-editor]')?.classList.toggle('show-edit', next);
      return next;
    });
  };

  const handleSaveOfferEditor = async () => {
    const frameDocument = offerFrameRef.current?.contentDocument;
    if (!offerEditor?.documentId || !frameDocument) return;
    const root = frameDocument.querySelector('[data-document-editor]');
    const fields = {};
    frameDocument.querySelectorAll('[data-field]').forEach((element) => {
      const field = element.getAttribute('data-field');
      if (field && !(field in fields)) fields[field] = element.innerText.trim();
    });
    frameDocument.querySelectorAll('[data-toggle]').forEach((element) => {
      const field = element.getAttribute('data-toggle');
      if (field) fields[field] = Boolean(element.checked);
    });
    if (root && financialInlineDocTypes.has(root.dataset.documentType)) {
      fields.line_items = invoiceLineItems(root);
    }
    try {
      setOfferEditorSaving(true);
      await saveApplicationDocument(offerEditor.documentId, fields);
      toast.success('Document saved as a new version.');
      setOfferEditor(null);
      await loadDocuments();
      window.dispatchEvent(new Event('portal-documents-changed'));
    } catch (err) {
      console.error(err);
      toast.error(cleanError(err, 'Document could not be saved.'));
    } finally {
      setOfferEditorSaving(false);
    }
  };

  const renderTextInput = (key, label, type = 'text') => (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Input
        type={type}
        value={type === 'date' ? formDate(issueInputs[key]) : formText(issueInputs[key])}
        onChange={(e) => updateIssueInput(key, e.target.value)}
        className={inputClass}
      />
    </div>
  );

  const renderNumberInput = (key, label) => (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Input
        type="number"
        min="0"
        step="0.01"
        value={formNumber(issueInputs[key])}
        onChange={(e) => updateIssueInput(key, e.target.value)}
        className={inputClass}
      />
    </div>
  );

  const renderTextarea = (key, label, rows = 3) => (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Textarea
        value={formText(issueInputs[key])}
        onChange={(e) => updateIssueInput(key, e.target.value)}
        className={inputClass}
        rows={rows}
      />
    </div>
  );

  const renderBooleanSelect = (key, label) => (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Select value={formBool(issueInputs[key])} onValueChange={(value) => updateIssueInput(key, value === 'true')}>
        <SelectTrigger className={inputClass}><SelectValue /></SelectTrigger>
        <SelectContent>
          {yesNoOptions.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  const renderGroupTitle = (label) => (
    <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#062B1A]/45">{label}</p>
  );

  const renderChecklist = () => {
    const rows = safeList(issueInputs.document_checklist);
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <Label>Document Checklist</Label>
          <Button type="button" variant="outline" size="sm" onClick={() => addListRow('document_checklist', { label: '', status: 'Pending' })}>
            <Plus className="w-3.5 h-3.5 mr-1" /> Add
          </Button>
        </div>
        {rows.length === 0 ? (
          <p className="rounded-lg border border-dashed border-[rgba(6,43,26,0.16)] px-3 py-4 text-sm text-[#062B1A]/45">
            No checklist rows.
          </p>
        ) : (
          <div className="space-y-2">
            {rows.map((row, index) => (
              <div key={`${row.label || 'checklist'}-${index}`} className="grid grid-cols-1 gap-2 rounded-lg border border-[rgba(6,43,26,0.08)] p-3 sm:grid-cols-[1fr_170px_auto]">
                <Input
                  value={formText(row.label)}
                  onChange={(e) => updateListRow('document_checklist', index, 'label', e.target.value)}
                  placeholder="Requirement"
                  className={inputClass}
                />
                <Select value={formText(row.status) || 'Pending'} onValueChange={(value) => updateListRow('document_checklist', index, 'status', value)}>
                  <SelectTrigger className={inputClass}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {checklistStatusOptions.map((option) => <SelectItem key={option} value={option}>{option}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button type="button" variant="outline" size="icon" onClick={() => removeListRow('document_checklist', index)} aria-label="Remove checklist row">
                  <Minus className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderFeeRows = (key, label) => {
    const rows = safeList(issueInputs[key]);
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <Label>{label}</Label>
          <Button type="button" variant="outline" size="sm" onClick={() => addListRow(key, { amount: 0, label: '' })}>
            <Plus className="w-3.5 h-3.5 mr-1" /> Add
          </Button>
        </div>
        {rows.length === 0 ? (
          <p className="rounded-lg border border-dashed border-[rgba(6,43,26,0.16)] px-3 py-4 text-sm text-[#062B1A]/45">
            No fee rows.
          </p>
        ) : (
          <div className="space-y-2">
            {rows.map((row, index) => (
              <div key={`${key}-${index}`} className="grid grid-cols-1 gap-2 rounded-lg border border-[rgba(6,43,26,0.08)] p-3 sm:grid-cols-[140px_1fr_auto]">
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formNumber(row.amount)}
                  onChange={(e) => updateListRow(key, index, 'amount', e.target.value)}
                  className={inputClass}
                />
                <Input
                  value={formText(row.label)}
                  onChange={(e) => updateListRow(key, index, 'label', e.target.value)}
                  placeholder="Label"
                  className={inputClass}
                />
                <Button type="button" variant="outline" size="icon" onClick={() => removeListRow(key, index)} aria-label={`Remove ${label} row`}>
                  <Minus className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderLineItems = () => {
    const rows = safeList(issueInputs.line_items);
    const amountDue = rows.reduce(
      (sum, row) => sum + (Number(row.quantity) || 0) * (Number(row.rate) || 0),
      0,
    );
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <Label>Line Items</Label>
          <Button type="button" variant="outline" size="sm" onClick={() => addListRow('line_items', { subject: '', description: '', quantity: 1, rate: 0 })}>
            <Plus className="w-3.5 h-3.5 mr-1" /> Add line
          </Button>
        </div>
        {rows.length === 0 ? (
          <p className="rounded-lg border border-dashed border-[rgba(6,43,26,0.16)] px-3 py-4 text-sm text-[#062B1A]/45">
            No line items.
          </p>
        ) : (
          <div className="space-y-3">
            {rows.map((row, index) => (
              <div key={`line-${index}`} className="space-y-2 rounded-lg border border-[rgba(6,43,26,0.08)] p-3">
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_90px_120px_auto]">
                  <Input
                    value={formText(row.subject)}
                    onChange={(e) => updateListRow('line_items', index, 'subject', e.target.value)}
                    placeholder="Subject"
                    className={inputClass}
                  />
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={formNumber(row.quantity)}
                    onChange={(e) => updateListRow('line_items', index, 'quantity', e.target.value)}
                    placeholder="Qty"
                    className={inputClass}
                  />
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formNumber(row.rate)}
                    onChange={(e) => updateListRow('line_items', index, 'rate', e.target.value)}
                    placeholder="Rate"
                    className={inputClass}
                  />
                  <Button type="button" variant="outline" size="icon" onClick={() => removeListRow('line_items', index)} aria-label="Remove line item">
                    <Minus className="w-4 h-4" />
                  </Button>
                </div>
                <Textarea
                  value={formText(row.description)}
                  onChange={(e) => updateListRow('line_items', index, 'description', e.target.value)}
                  placeholder="Description"
                  className={inputClass}
                  rows={2}
                />
              </div>
            ))}
          </div>
        )}
        <p className="text-right text-sm font-semibold text-[#062B1A]">
          Amount Due (CAD): {amountDue.toLocaleString(undefined, { style: 'currency', currency: 'CAD' })}
        </p>
      </div>
    );
  };

  const renderInvoiceForm = () => (
    <div className="space-y-6">
      <div className="space-y-3">
        {renderGroupTitle('Bill to')}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {renderTextInput('bill_to_name', 'Name')}
          {renderTextInput('bill_to_phone', 'Phone')}
          {renderTextInput('bill_to_email', 'Email')}
        </div>
        {renderTextarea('bill_to_address', 'Address', 3)}
      </div>

      <div className="space-y-3">
        {renderGroupTitle('Invoice details')}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {renderTextInput('invoice_number', 'Invoice Number')}
          {renderTextInput('invoice_date', 'Invoice Date', 'date')}
          {renderTextInput('payment_due', 'Payment Due', 'date')}
        </div>
      </div>

      <div className="space-y-3">
        {renderGroupTitle('Line items')}
        {renderLineItems()}
      </div>
    </div>
  );

  const renderReceiptForm = () => (
    <div className="space-y-6">
      <div className="space-y-3">
        {renderGroupTitle('Bill to')}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {renderTextInput('bill_to_name', 'Name')}
          {renderTextInput('bill_to_street', 'Street / Address')}
          {renderTextInput('bill_to_locality', 'City / Province')}
          {renderTextInput('bill_to_country', 'Country')}
        </div>
      </div>

      <div className="space-y-3">
        {renderGroupTitle('Receipt details')}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {renderTextInput('invoice_number', 'Invoice Number (from invoice)')}
          {renderTextInput('invoice_date', 'Invoice Date (from invoice)', 'date')}
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {renderTextInput('payment_date', 'Payment Date', 'date')}
          {renderTextInput('method_of_payment', 'Method of Payment')}
        </div>
      </div>

      <div className="space-y-3">
        {renderGroupTitle('Line items')}
        {renderLineItems()}
      </div>
    </div>
  );

  const renderFeeForm = () => (
    <div className="space-y-6">
      <div className="space-y-3">
        {renderGroupTitle('Contact')}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {renderTextInput('finance_email', 'Finance Email')}
          {renderTextInput('etransfer_password', 'E-Transfer Password')}
        </div>
      </div>

      <div className="space-y-3">
        {renderGroupTitle('Bank account information')}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {renderTextInput('bank_account_name', 'Name on Account')}
          {renderTextInput('bank_institution', 'Bank Institution Name')}
          {renderTextInput('bank_address', 'Bank Address')}
          {renderTextInput('bank_phone', 'Bank Phone Number')}
          {renderTextInput('account_number', 'Account Number')}
          {renderTextInput('transit_number', 'Branch Transit Number')}
          {renderTextInput('swift_code', 'SWIFT Code')}
          {renderTextInput('cc_number', 'CC Number')}
        </div>
      </div>
    </div>
  );

  const renderOfferForm = () => (
    <div className="space-y-6">
      <div className="space-y-3">
        {renderGroupTitle('Dates and identifiers')}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {renderTextInput('date_of_issue', 'Date of Issue', 'date')}
          {renderTextInput('payment_deadline', 'Payment Deadline', 'date')}
          {renderTextInput('application_number', 'Application Number')}
        </div>
      </div>

      <div className="space-y-3">
        {renderGroupTitle('Applicant')}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {renderTextInput('student_name', 'Full Name')}
          {renderTextInput('given_name', 'Given Name')}
          {renderTextInput('family_name', 'Family Name')}
        </div>
        {renderTextarea('mailing_address', 'Mailing Address', 4)}
      </div>

      <div className="space-y-3">
        {renderGroupTitle('Program')}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {renderTextInput('program_name', 'Program')}
          {renderTextInput('duration_of_program', 'Duration')}
          {renderTextInput('start_date', 'Intended Start')}
          {renderTextInput('completion_date', 'Anticipated Completion')}
        </div>
        {renderTextarea('prerequisites_condition', 'Prerequisites Condition')}
        {renderTextarea('scholarship_bursary', 'Scholarship / Bursary')}
      </div>

      <div className="space-y-3">
        {renderGroupTitle('English proficiency')}
        {renderTextarea('english_proficiency_text', 'English Proficiency Text', 4)}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {renderTextInput('english_proficiency_label', 'Proficiency Label')}
          {renderTextInput('english_proficiency_score', 'Score')}
        </div>
      </div>

      <div className="space-y-3">
        {renderGroupTitle('Fees and checklist')}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {renderTextarea('min_fee_heading', 'Minimum Fee Heading', 3)}
          {renderTextarea('recommended_fee_heading', 'Recommended Fee Heading', 3)}
        </div>
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          {renderFeeRows('min_fees', 'Minimum Fees')}
          {renderFeeRows('recommended_fees', 'Recommended Fees')}
        </div>
        {renderChecklist()}
      </div>

      <div className="space-y-3">
        {renderGroupTitle('Signatory')}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {renderTextInput('signatory_name', 'Name')}
          {renderTextInput('signatory_title', 'Title')}
          {renderTextInput('dli_number', 'DLI')}
        </div>
      </div>
    </div>
  );

  const renderIssueForm = () => {
    if (issueLoading) {
      return (
        <div className="flex items-center justify-center gap-2 py-12 text-sm text-[#062B1A]/55">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading defaults...
        </div>
      );
    }

    if (issueType === 'offer_letter') return renderOfferForm();
    if (issueType === 'invoice') return renderInvoiceForm();
    if (issueType === 'receipt') return renderReceiptForm();
    if (issueType === 'fee_payment_instructions') return renderFeeForm();

    if (issueType === 'loa') {
      return (
        <div className="space-y-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {renderTextInput('date_of_issue', 'Date of Issue', 'date')}
            {renderTextInput('field_of_study', 'Field of Study')}
            {renderTextInput('training_type', 'Training Type')}
            {renderTextInput('level_of_study', 'Level of Study')}
            {renderTextInput('length_of_program', 'Length of Program')}
            {renderNumberInput('hours_per_week', 'Hours per Week')}
            {renderNumberInput('estimated_first_year_tuition', 'Estimated First Year Tuition')}
            {renderNumberInput('prepaid_tuition', 'Prepaid Tuition')}
            {renderNumberInput('scholarship_amount', 'Scholarship Amount')}
            {renderBooleanSelect('caq', 'CAQ')}
            {renderBooleanSelect('conditions', 'Conditions')}
          </div>
          {renderTextarea('prerequisites_condition', 'Prerequisites Condition')}
          {renderTextarea('pathway_opportunity', 'Pathway Opportunity')}
          {renderTextarea('conditions_text', 'Conditions Text')}
          {renderTextarea('other_relevant_info', 'Other Relevant Info')}
        </div>
      );
    }

    if (issueType === 'transcript') {
      return (
        <div className="space-y-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {renderTextInput('date_of_issue', 'Date of Issue', 'date')}
            {renderTextInput('academic_standing', 'Academic Standing')}
            {renderTextInput('cumulative_standing', 'Cumulative Standing')}
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <Card className="official-documents-card border border-[rgba(6, 43, 26,0.08)] shadow-sm">
      <CardHeader className="pb-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="text-base text-[#062B1A] flex items-center gap-2" style={{ fontFamily: 'var(--font-display)' }}>
              <FileCheck2 className="w-4 h-4" /> {title}
            </CardTitle>
            {description && <p className="mt-1 text-xs text-[#062B1A]/50">{description}</p>}
          </div>
          <Button type="button" variant="outline" size="sm" onClick={loadDocuments} disabled={loading || !ownerId}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {error && <p className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>}
        {loading ? (
          <div className="flex items-center gap-2 py-6 text-sm text-[#062B1A]/55">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading documents...
          </div>
        ) : (
          docTypes.map((docType) => {
            if (!isApplication && docType.value === 'invoice') {
              return <StudentDocumentInvoices key="invoice" studentId={ownerId} />;
            }
            const group = documents?.[docType.value] || { label: docType.label, active: null, history: [] };
            const active = group.active;
            const history = safeList(group.history);
            const latest = newestDocument(history);
            const isHistoryOpen = Boolean(historyOpen[docType.value]);
            const isInlineDocument = isApplication
              ? inlineApplicationDocTypes.has(docType.value)
              : inlineStudentDocTypes.has(docType.value);
            const statusText = active
              ? `${titleCaseStatus(active.status)} v${active.version || 1}${active.status === 'issued' ? ` on ${formatDate(active.issued_at)}` : ''}`
              : latest?.status === 'revoked'
                ? `Revoked on ${formatDate(latest.revoked_at)}`
                : isApplication
                  ? 'No draft'
                  : 'Not issued';
            const badgeClass = active?.status === 'issued'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
              : active?.status === 'draft'
                ? 'border-amber-200 bg-amber-50 text-amber-700'
                : latest?.status === 'revoked'
                  ? 'border-rose-200 bg-rose-50 text-rose-700'
                  : 'border-[#062B1A]/15 bg-[#F7F8FA] text-[#062B1A]/55';
            const primaryLabel = isInlineDocument
              ? active ? 'Edit' : 'Generate draft'
              : isApplication
                ? active ? 'Regenerate' : 'Generate draft'
              : active ? 'Prepare revision' : 'Generate draft';

            return (
              <div key={docType.value} className="rounded-lg border border-[rgba(6,43,26,0.08)] bg-white/70 p-4">
                <div className="document-row grid gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-medium text-[#062B1A]">{docType.label}</p>
                      <Badge variant="outline" className={badgeClass}>{statusText}</Badge>
                    </div>
                    <p className="mt-1 break-words text-xs text-[#062B1A]/45">
                      {active
                        ? `${active.status === 'draft' ? 'Prepared' : 'Issued'} by ${displayText(active.issued_by_name, 'Admin')}${active.file_name ? ` - ${active.file_name}` : ''}`
                        : history.length
                          ? `${history.length} archived version${history.length === 1 ? '' : 's'}`
                          : isApplication
                            ? `No application-owned ${docType.label} has been generated yet.`
                            : 'No official version has been issued yet.'}
                    </p>
                    {active?.last_send_status && <p className={`mt-1 text-xs ${active.last_send_status === 'failed' ? 'text-red-700' : 'text-[#062B1A]/50'}`}>Email: {active.last_send_status === 'failed' ? 'Failed' : 'Sent'} · {formatDateTime(active.last_send_attempt_at)}{active.last_send_error ? ` · ${active.last_send_error}` : ''}</p>}
                    {ownerType === 'student' && active?.status === 'draft' && (
                      <p className="mt-1 text-xs text-amber-700">
                        {history.some((doc) => doc.id !== active.id && doc.status === 'issued')
                          ? 'The student keeps the confirmed version until you confirm this draft.'
                          : 'The student cannot see or download this until you confirm it.'}
                      </p>
                    )}
                  </div>
                  <div className="document-row-actions flex flex-wrap items-start gap-2">
                    <Button
                      type="button"
                      size="sm"
                      disabled={isInlineDocument && issueSubmitting}
                      onClick={() => {
                        if (!isInlineDocument) {
                          openIssueDialog(docType.value);
                        } else if (active) {
                          openOfferEditor(active.id);
                        } else {
                          handleGenerateInlineDocument(docType.value);
                        }
                      }}
                      className="bg-[#062B1A] text-white hover:bg-[#041B10]"
                    >
                      {isInlineDocument && active
                        ? <Pencil className="w-4 h-4 mr-2" />
                        : issueSubmitting && isInlineDocument
                          ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          : <FileCheck2 className="w-4 h-4 mr-2" />}
                      {primaryLabel}
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => handleDownload(active.id)} disabled={!active || downloadingId === active?.id}>
                      {downloadingId === active?.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
                      Download
                    </Button>
                    {isInlineDocument && active && (
                      <Button type="button" variant="outline" size="sm" disabled={Boolean(changingId)} onClick={() => handleDocumentState(active, active.status === 'draft' ? 'confirm' : 'reopen')}>
                        {active.status === 'draft' ? 'Confirm without sending' : 'Return to draft'}
                      </Button>
                    )}
                    {!isInlineDocument && (
                      <Button type="button" variant="outline" size="sm" onClick={() => handleRevoke(active)} disabled={!active || revokingId === active?.id}>
                        {revokingId === active?.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Ban className="w-4 h-4 mr-2" />}
                        Revoke
                      </Button>
                    )}
                    {history.length > 0 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setHistoryOpen((current) => ({ ...current, [docType.value]: !current[docType.value] }))}
                      >
                        <History className="w-4 h-4 mr-2" /> History
                      </Button>
                    )}
                  </div>
                </div>
                {isHistoryOpen && history.length > 0 && (
                  <div className="mt-4 space-y-2 border-t border-[rgba(6,43,26,0.08)] pt-3">
                    {history.map((doc) => (
                      <div key={doc.id} className="flex flex-col gap-2 rounded-lg bg-[#F7F8FA] px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <p className="text-sm font-medium text-[#062B1A]">
                            v{doc.version || 1} - {titleCaseStatus(doc.status)}
                          </p>
                          <p className="text-xs text-[#062B1A]/45">
                            {[formatDateTime(doc.issued_at || doc.created_at), doc.status === 'revoked' ? `revoked ${formatDateTime(doc.revoked_at)}` : '', doc.file_name].filter(Boolean).join(' - ')}
                            {doc.sent_at ? ` · Emailed ${formatDateTime(doc.sent_at)} to ${doc.sent_to || 'student'}` : ''}
                          </p>
                        </div>
                        <Button type="button" variant="outline" size="sm" onClick={() => handleDownload(doc.id)} disabled={downloadingId === doc.id}>
                          {downloadingId === doc.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
                          Download
                        </Button>
                        {isInlineDocument && doc.id !== active?.id && <Button type="button" variant="outline" size="sm" disabled={Boolean(changingId)} onClick={() => handleDocumentState(doc, 'restore', active)}>Restore version</Button>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </CardContent>

      <Dialog open={Boolean(issueType)} onOpenChange={(open) => (open ? null : closeIssueDialog())}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>{issueType ? `Prepare ${issuedDocumentLabel(docTypes, issueType)}` : 'Prepare document'}</DialogTitle>
            <DialogDescription>Save a draft, review it, then confirm it when ready. Earlier versions remain in history.</DialogDescription>
          </DialogHeader>
          {renderIssueForm()}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeIssueDialog} disabled={issueSubmitting}>Cancel</Button>
            <Button type="button" onClick={handleIssue} disabled={issueLoading || issueSubmitting} className="bg-[#062B1A] text-white hover:bg-[#041B10]">
              {issueSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileCheck2 className="w-4 h-4 mr-2" />}
              Save Draft
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(offerEditor)} onOpenChange={(open) => (open ? null : closeOfferEditor())}>
        <DialogContent className="flex h-[94vh] max-w-[calc(100vw-1rem)] flex-col overflow-hidden p-4 sm:max-w-[calc(100vw-3rem)] sm:p-6">
          <DialogHeader>
            <DialogTitle>Inline Document Editor</DialogTitle>
            <DialogDescription>{offerEditor?.status === 'issued' ? 'Save and confirm a revision of this document. Its previous version and sending history remain available.' : 'Save a draft version for review. Confirm it when ready.'}</DialogDescription>
          </DialogHeader>
          {offerEditorLoading ? (
            <div className="flex flex-1 items-center justify-center gap-2 text-sm text-[#062B1A]/55">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading the current document version...
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[rgba(6,43,26,0.10)] bg-[#F7F8FA] px-3 py-2">
                <p className="text-xs text-[#062B1A]/60">Click highlighted fields to edit them. Invoice lines and document options can be adjusted where available.</p>
                <Button type="button" size="sm" variant="outline" onClick={toggleOfferHighlights}>
                  <Highlighter className="mr-2 h-4 w-4" /> Edit highlights: {showOfferHighlights ? 'on' : 'off'}
                </Button>
              </div>
              <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-[rgba(6,43,26,0.14)] bg-[#5c5f66]">
                <iframe
                  key={offerEditor?.documentId}
                  ref={offerFrameRef}
                  title={offerEditor?.label || 'Offer Letter'}
                  srcDoc={offerEditor?.html || ''}
                  sandbox="allow-same-origin"
                  onLoad={handleOfferFrameLoad}
                  style={{ height: offerEditor?.docType === 'enrolment_contract' ? 3400 : offerEditor?.docType === 'transcript' ? 3200 : 2250 }}
                  className="block w-full border-0"
                />
              </div>
            </>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeOfferEditor} disabled={offerEditorSaving}>Cancel</Button>
            <Button
              type="button"
              onClick={handleSaveOfferEditor}
              disabled={offerEditorLoading || offerEditorSaving}
              className="bg-[#062B1A] text-white hover:bg-[#041B10]"
            >
              {offerEditorSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              {offerEditor?.status === 'issued' ? 'Save & confirm revision' : 'Save new draft version'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
